import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { themeColors } from '@/config/theme/themeColors.ts';
import { ResolvedAnnouncement } from '@/contexts/announcements/model/Announcements.model.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';

type CardSize = {
  height: number;
  width: number;
};

type Layout = {
  cardLeft: number;
  cardTop: number;
  highlightBorderRadius: number;
  highlightHeight: number;
  highlightLeft: number;
  highlightTop: number;
  highlightWidth: number;
};

type Props = {
  anchorElement: HTMLElement;
  announcement: ResolvedAnnouncement;
  currentIndex: number;
  onClose: () => void;
  onSkip: () => void;
  total: number;
};

const VIEWPORT_OFFSET = 16;
const HIGHLIGHT_PADDING = 0;
const HIGHLIGHT_RING_OFFSET = 2;

export const AnnouncementsCoachmark: React.FC<Props> = ({
  anchorElement,
  announcement,
  currentIndex,
  onClose,
  onSkip,
  total,
}) => {
  const { t } = useTranslationWithPrefix('components.announcements');
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
  const [cardSize, setCardSize] = useState<CardSize>({ height: 220, width: 340 });

  const clearAnchorRect = () => {
    setAnchorRect(null);
  };

  const updateAnchorRect = (nextRect: DOMRect) => {
    setAnchorRect(nextRect);
  };

  const updateCardSize = (nextCardSize: CardSize) => {
    setCardSize(nextCardSize);
  };

  useLayoutEffect(() => {
    const updateLayout = () => {
      const nextRect = anchorElement.getBoundingClientRect();

      if (nextRect.width === 0 || nextRect.height === 0) {
        clearAnchorRect();
        return;
      }

      updateAnchorRect(nextRect);

      if (cardRef.current) {
        updateCardSize({
          height: cardRef.current.offsetHeight,
          width: cardRef.current.offsetWidth,
        });
      }
    };

    updateLayout();

    const resizeObserver = new ResizeObserver(updateLayout);
    resizeObserver.observe(anchorElement);

    if (cardRef.current) {
      resizeObserver.observe(cardRef.current);
    }

    globalThis.addEventListener('resize', updateLayout);
    globalThis.addEventListener('scroll', updateLayout, true);

    return () => {
      resizeObserver.disconnect();
      globalThis.removeEventListener('resize', updateLayout);
      globalThis.removeEventListener('scroll', updateLayout, true);
    };
  }, [anchorElement, announcement.announcementId]);

  useEffect(() => {
    if (!anchorRect) {
      return;
    }

    if (
      anchorRect.bottom < 0 ||
      anchorRect.top > globalThis.innerHeight ||
      anchorRect.right < 0 ||
      anchorRect.left > globalThis.innerWidth
    ) {
      clearAnchorRect();
    }
  }, [anchorRect]);

  const layout = useMemo<Layout | null>(() => {
    if (!anchorRect) {
      return null;
    }

    const highlightLeft = Math.max(anchorRect.left - HIGHLIGHT_PADDING, VIEWPORT_OFFSET);
    const highlightTop = Math.max(anchorRect.top - HIGHLIGHT_PADDING, VIEWPORT_OFFSET);
    const highlightWidth = Math.min(
      anchorRect.width + HIGHLIGHT_PADDING * 2,
      globalThis.innerWidth - highlightLeft - VIEWPORT_OFFSET,
    );
    const highlightHeight = Math.min(
      anchorRect.height + HIGHLIGHT_PADDING * 2,
      globalThis.innerHeight - highlightTop - VIEWPORT_OFFSET,
    );
    const preferredTop = highlightTop + highlightHeight + 16;
    const fitsBelow = preferredTop + cardSize.height <= globalThis.innerHeight - VIEWPORT_OFFSET;
    const cardTop = fitsBelow
      ? preferredTop
      : Math.max(highlightTop - cardSize.height - 16, VIEWPORT_OFFSET);
    const cardLeft = clamp(
      highlightLeft + highlightWidth / 2 - cardSize.width / 2,
      VIEWPORT_OFFSET,
      globalThis.innerWidth - cardSize.width - VIEWPORT_OFFSET,
    );
    const computedBorderRadius = Number.parseFloat(
      globalThis.getComputedStyle(anchorElement).borderRadius || '0',
    );

    return {
      cardLeft,
      cardTop,
      highlightBorderRadius: Number.isNaN(computedBorderRadius) ? 12 : computedBorderRadius + 2,
      highlightHeight,
      highlightLeft,
      highlightTop,
      highlightWidth,
    };
  }, [anchorElement, anchorRect, cardSize.height, cardSize.width]);

  if (!layout) {
    return null;
  }

  return createPortal(
    <Stack sx={{ inset: 0, pointerEvents: 'none', position: 'fixed', zIndex: 2000 }}>
      <Stack
        sx={{
          backgroundColor: 'rgba(17, 17, 17, 0.42)',
          inset: 0,
          pointerEvents: 'auto',
          position: 'absolute',
        }}
      />
      <Stack
        sx={{
          borderRadius: `${layout.highlightBorderRadius}px`,
          boxShadow: `0 0 0 ${HIGHLIGHT_RING_OFFSET}px rgba(255,255,255,0), 0 0 0 ${HIGHLIGHT_RING_OFFSET + 2}px ${themeColors.yellow}, 0 0 0 9999px rgba(17, 17, 17, 0.16)`,
          height: layout.highlightHeight,
          left: layout.highlightLeft,
          pointerEvents: 'none',
          position: 'fixed',
          top: layout.highlightTop,
          transition: 'all 180ms ease',
          width: layout.highlightWidth,
        }}
      />
      <Paper
        elevation={8}
        ref={cardRef}
        sx={{
          background: 'linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(247,247,248,1) 100%)',
          border: `1px solid ${themeColors.stroke}`,
          left: layout.cardLeft,
          maxWidth: `calc(100vw - ${VIEWPORT_OFFSET * 2}px)`,
          p: 2.5,
          pointerEvents: 'auto',
          position: 'fixed',
          top: layout.cardTop,
          transition: 'all 180ms ease',
          width: 340,
        }}
      >
        <Stack spacing={2}>
          <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography sx={{ color: themeColors.greyDark, fontSize: 13, fontWeight: 700 }}>
              {t('coachmark.progress', {
                current: currentIndex + 1,
                total,
              })}
            </Typography>
            <Button color="inherit" onClick={onSkip} size="small" variant="text">
              {t('coachmark.button.skip')}
            </Button>
          </Stack>
          <Stack spacing={1}>
            <Typography sx={{ fontSize: 18, fontWeight: 700 }}>{announcement.title}</Typography>
            <Typography sx={{ color: themeColors.greyDark }}>{announcement.description}</Typography>
          </Stack>
          <Stack direction="row" sx={{ justifyContent: 'flex-end' }}>
            <Button onClick={onClose} variant="contained">
              {currentIndex + 1 === total
                ? t('coachmark.button.close')
                : t('coachmark.button.next')}
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Stack>,
    document.body,
  );
};

const clamp = (value: number, min: number, max: number) => {
  return Math.min(Math.max(value, min), max);
};
